import { useState, type ReactNode } from 'react';
import { InvoiceTable } from '@/components/billing/InvoiceTable';
import { InvoiceCards } from '@/components/billing/InvoiceCards';
import { WORKSPACE_COLUMNS } from '@/components/billing/invoiceRow';
import type { InvoiceRowActionHandlers } from '@/components/billing/InvoiceTable';
import { PaymentsPanel } from '@/components/billing/PaymentsPanel';
import { SendInvoiceDialog } from '@/components/billing/SendInvoiceDialog';
import { InvoicePdfViewerDialog } from '@/components/billing/InvoicePdfViewerDialog';
import { downloadInvoicePdf } from '@/hooks/useInvoices';
import { useToast } from '@/hooks/use-toast';
import type { WorkspaceInvoiceRow, WorkspaceStage } from '@jobos/shared';
import { useWorkspaceInvoiceActions } from '../hooks/useWorkspaceInvoiceActions';
import { agingGroupSummary, agingGroups } from './agingGroups';

/**
 * Stages 2-4 — Drafts, Sent and Paid.
 *
 * All three are the same shared InvoiceTable / InvoiceCards with the same
 * actions; what differs is which rows the server returned and, on Sent,
 * the aging group headers.
 */

export interface InvoiceStageProps {
  stage: WorkspaceStage;
  rows: WorkspaceInvoiceRow[];
  emptyState: ReactNode;
  onOpenJob: (row: WorkspaceInvoiceRow) => void;
}

export function InvoiceStage({ stage, rows, emptyState, onOpenJob }: InvoiceStageProps) {
  const { toast } = useToast();
  const { markSent, setSentDate, voidInvoice, deleteInvoice, invalidate } =
    useWorkspaceInvoiceActions();

  // Which invoice the payments panel is open for; null = closed.
  const [payingFor, setPayingFor] = useState<WorkspaceInvoiceRow | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  // Same shape for the send dialog: which invoice it is open for, null = closed.
  const [sendingFor, setSendingFor] = useState<WorkspaceInvoiceRow | null>(null);
  // And for the PDF viewer.
  const [viewingPdfFor, setViewingPdfFor] = useState<WorkspaceInvoiceRow | null>(null);

  const openPayments = (row: WorkspaceInvoiceRow, startRecording: boolean) => {
    setPayingFor(row);
    setRecordOpen(startRecording);
  };

  const handleDownloadPdf = async (row: WorkspaceInvoiceRow) => {
    // The menu closes on click, so progress is a toast — the PDF is fetched or
    // rendered server-side, which can take a beat.
    const progress = toast({ title: 'Downloading PDF…' });
    try {
      await downloadInvoicePdf(row.invoiceId, row.invoiceNumber);
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the invoice PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      progress.dismiss();
    }
  };

  const actionsFor = (row: WorkspaceInvoiceRow): InvoiceRowActionHandlers => ({
    onRecordPayment: () => openPayments(row, true),
    onViewPayments: () => openPayments(row, false),
    onMarkSent: () => markSent.mutate(row.invoiceId),
    onSetSentDate: (date) => setSentDate.mutate({ invoiceId: row.invoiceId, date }),
    onSend: () => setSendingFor(row),
    onVoid: () => voidInvoice.mutate(row.invoiceId),
    onDelete: () => deleteInvoice.mutate(row.invoiceId),
    onViewPdf: () => setViewingPdfFor(row),
    onDownloadPdf: () => handleDownloadPdf(row),
    // Neither rebuild handler is passed, so no invoice here offers Regenerate
    // or Edit invoice: rebuilding a final from the job's current breakdown and
    // re-scoping a partial are both job-page decisions, made while looking at
    // that breakdown. The job Billing tab passes both.
    //
    // Amending a migrated invoice's amount is likewise a job-page action —
    // the row links there, and the workspace does not fork the edit flow.
  });

  // Only stage 3 groups by aging: it is the worklist. Drafts have never been
  // sent and Paid rows are settled, so neither ages.
  const groups = stage === 'sent' ? agingGroups(rows) : null;
  const groupHeaderFor = groups
    ? (row: WorkspaceInvoiceRow) => {
        const group = groups.get(row.invoiceId);
        if (!group) return null;
        return (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
              {group.label}
            </span>
            <span className="text-xs tabular-nums text-gray-500">{agingGroupSummary(group)}</span>
          </div>
        );
      }
    : undefined;

  const shared = {
    rows,
    /*
     * Age only on Sent. On To Send an invoice has not gone out, so it ages from
     * nothing and the cell is an em dash on every row; on Paid the money is in,
     * and `daysOutstanding` is deliberately nulled there (invoice-ledger's
     * agesCondition), so that column is an em dash too. A column that is blank
     * for every row of a stage is noise taking width from the figures beside it.
     */
    columns: { ...WORKSPACE_COLUMNS, aging: stage === 'sent' },
    actionsFor,
    groupHeaderFor,
    emptyState,
    onRowClick: onOpenJob,
  };

  return (
    <>
      <div className="hidden xl:block">
        <InvoiceTable {...shared} />
      </div>
      <div className="xl:hidden">
        <InvoiceCards {...shared} />
      </div>

      {/* The shared ledger panel — the job Billing tab opens the same one. */}
      <PaymentsPanel
        key={payingFor?.invoiceId}
        invoice={payingFor}
        startRecording={recordOpen}
        onClose={() => setPayingFor(null)}
        // A settled invoice must move from Sent to Paid without a refresh,
        // which means the tab counts move with it.
        onRecorded={invalidate}
      />

      {/* Keyed so a second invoice fetches its own PDF rather than showing the
          last one's while the new bytes are still in flight. */}
      <InvoicePdfViewerDialog
        key={viewingPdfFor?.invoiceId}
        invoice={viewingPdfFor}
        onClose={() => setViewingPdfFor(null)}
      />

      {/* Mounted only while open — the dialog asks the server to compose an
          email preview, which a closed one has no business doing. Keyed so a
          second invoice opens with its own draft rather than the last one's. */}
      {sendingFor && (
        <SendInvoiceDialog
          key={sendingFor.invoiceId}
          open
          onOpenChange={(next) => !next && setSendingFor(null)}
          invoiceId={sendingFor.invoiceId}
          invoiceNumber={sendingFor.invoiceNumber}
          // Feeds the hook's invalidation: a send moves the job's rollup too.
          jobId={sendingFor.jobId}
          // useSendInvoice drops the invoice and job caches, but knows nothing
          // of `workspaceKeys` — without this the sent draft stays in Drafts.
          onSent={invalidate}
        />
      )}
    </>
  );
}
